import type Pusher from "pusher-js";
import { getPusher } from "../pusher";
import type { TransportAirlineApiContext } from "./airlineTransportContext";

const DEFAULT_RESULT_EVENT = "estimation.result";
const DEFAULT_ERROR_EVENT = "estimation.error";
const DEFAULT_TIMEOUT_MS = 15000;
const DEFAULT_CHANNEL_PREFIX = "estimation";

export type PusherEstimationConfig = {
  pusher: Pusher;
  timeoutMs?: number;
  channelPrefix?: string;
  eventName?: string;
  errorEventName?: string;
};

export function getAirlinePusherConfig(
  transportContext: TransportAirlineApiContext,
  global: { soketiAppKey: string | null; soketiWsHost: string | null }
): PusherEstimationConfig | undefined {
  if (transportContext.mode !== "airline") {
    return undefined;
  }
  const { soketiAppKey, soketiWsHost } = global;
  if (!soketiAppKey || !soketiWsHost) {
    return undefined;
  }
  return { pusher: getPusher(soketiAppKey, soketiWsHost) };
}

function parseEstimationEventData(data: unknown): any {
  if (typeof data === "string") {
    try {
      return JSON.parse(data);
    } catch {
      return { error: data };
    }
  }
  return data;
}

function waitForConnection(pusher: Pusher, timeoutMs: number): Promise<void> {
  if (pusher.connection.state === "connected") {
    return Promise.resolve();
  }

  return new Promise<void>((resolve, reject) => {
    const timeout = setTimeout(() => {
      pusher.connection.unbind("connected", onConnected);
      pusher.connection.unbind("failed", onFailed);
      reject(new Error("Pusher connection timed out."));
    }, timeoutMs);

    const onConnected = () => {
      clearTimeout(timeout);
      pusher.connection.unbind("failed", onFailed);
      resolve();
    };

    const onFailed = () => {
      clearTimeout(timeout);
      pusher.connection.unbind("connected", onConnected);
      reject(new Error("Pusher connection failed."));
    };

    pusher.connection.bind("connected", onConnected);
    pusher.connection.bind("failed", onFailed);
  });
}

function waitForSubscription(channel: any, timeoutMs: number): Promise<void> {
  if (channel.subscribed) {
    return Promise.resolve();
  }

  return new Promise<void>((resolve, reject) => {
    const timeout = setTimeout(() => {
      reject(new Error("Pusher subscription timed out."));
    }, timeoutMs);

    channel.bind("pusher:subscription_succeeded", () => {
      clearTimeout(timeout);
      resolve();
    });

    channel.bind("pusher:subscription_error", () => {
      clearTimeout(timeout);
      reject(new Error("Pusher subscription failed."));
    });
  });
}

export async function resolveEstimationResponse(apiResponseData: any, config?: PusherEstimationConfig): Promise<any> {
  const requestId = apiResponseData?.requestId;

  if (!requestId) {
    return apiResponseData;
  }

  if (!config?.pusher) {
    return apiResponseData;
  }

  const {
    pusher,
    timeoutMs = DEFAULT_TIMEOUT_MS,
    channelPrefix = DEFAULT_CHANNEL_PREFIX,
    eventName = DEFAULT_RESULT_EVENT,
    errorEventName = DEFAULT_ERROR_EVENT
  } = config;

  await waitForConnection(pusher, timeoutMs);

  const channelName = `${channelPrefix}.${requestId}`;
  const channel = pusher.subscribe(channelName);

  await waitForSubscription(channel, timeoutMs);

  return new Promise<any>((resolve, reject) => {
    let settled = false;

    const finish = (callback: () => void) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeout);
      channel.unbind(eventName, onResult);
      channel.unbind(errorEventName, onError);
      pusher.unsubscribe(channelName);
      callback();
    };

    const timeout = setTimeout(() => {
      finish(() => reject(new Error("Estimation timed out. Please try again.")));
    }, timeoutMs);

    const onError = (raw: unknown) => {
      const data = parseEstimationEventData(raw);
      finish(() => reject(new Error(data?.error || "Estimation failed.")));
    };

    const onResult = (raw: unknown) => {
      const data = parseEstimationEventData(raw);
      if (data?.error) {
        finish(() => reject(new Error(data.error)));
        return;
      }
      finish(() => resolve(data));
    };

    channel.bind(eventName, onResult);
    channel.bind(errorEventName, onError);
  });
}
